import { z } from 'zod';
import { ICollectionResponse } from './sync-monitoring.models';

/** Validates a single item against a schema; logs the details then throws a descriptive error on failure. */
export function parseResponseItem<T>(schema: z.ZodType<T>, item: unknown, endpoint: string, index?: number): T {
	const result = schema.safeParse(item);
	if (result.success) {
		return result.data;
	}

	const itemContext = index === undefined ? '' : ` (item index ${index})`;
	// Log loudly: the throw below is swallowed by the callers' catchError (so a section just
	// renders empty). Without this, a schema/API mismatch is invisible in the browser console.
	// `issues` is the structured field-level breakdown (path + message); `received` is the raw
	// payload that failed, so the exact offending field and value are diagnosable.
	console.error(`[SyncMonitoring] Invalid API response from ${endpoint}${itemContext}`, {
		issues: result.error.issues,
		received: item,
	});

	const error = new Error(`Invalid API response from ${endpoint}${itemContext}: ${result.error.message}`);
	(error as Error & { cause: z.ZodError }).cause = result.error;
	throw error;
}

/** Validates each item of a `{ self, items }` collection response, preserving the index in errors. */
export function parseCollection<T>(schema: z.ZodType<T>, response: ICollectionResponse<unknown>, endpoint: string): T[] {
	return response.items.map((item, index) => parseResponseItem(schema, item, endpoint, index));
}
